import { fetchAccessToken as fetchMsalAccessToken, msalInstance } from "@/config/msalConfig";
import { useUser as useClerkUser, useClerk, useAuth as useClerkAuth } from "@clerk/react";
import { useMsal, useIsAuthenticated as useMsalIsAuthenticated } from "@azure/msal-react";
import { createElement, Fragment, ReactNode } from "react";

// Clerk configuration for the Ila26 frontend.
//
// Phase 6 of the Azure -> Northflank migration: sits alongside msalConfig.ts
// during the incremental swap. In this initial PR the Clerk provider is
// mounted at the app root but does not drive auth yet — the MSAL provider
// still owns the flow. Subsequent PRs migrate callers of `fetchAccessToken`,
// `useMsal`, `<AuthenticatedTemplate>`, etc. and eventually delete MSAL.
//
// Behavior on the Azure DEV FE (front.dev.ila26.fr, running from the legacy
// ILA-26/Ilan26 repo): unchanged. That repo doesn't pick up this file.
// This file only ships in ILA-26/Ila26-next which deploys to Northflank.

export const clerkPublishableKey =
    (import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined) ?? "";

/**
 * The JWT template name configured in the Clerk dashboard (Dashboard ->
 * JWT Templates). The BE (Ilan26.Back) validates tokens whose payload
 * includes an `app_user_id` claim populated from Clerk's per-user
 * `external_id` field (which the ClerkMigration tool sets to the
 * ApplicationUser.Id). Ilan26.Front asks Clerk for this exact template
 * when it needs an API bearer.
 */
export const clerkJwtTemplateName = "ila26-api";

/**
 * True once VITE_CLERK_PUBLISHABLE_KEY is populated at build time. Falls
 * back to false so a dev / prod build without the env var still boots on
 * the MSAL path (rather than crashing on ClerkProvider missing its key).
 */
export const isClerkEnabled = clerkPublishableKey.length > 0;

/**
 * The shape RTK Query's prepareHeaders and any other non-React consumer
 * expects for an access token. Mirrors @azure/core-auth's `AccessToken`
 * so callers don't need to know which provider produced it.
 */
export interface FrontendAccessToken {
    token: string;
    expiresOnTimestamp: number;
}

declare global {
    interface Window {
        // ClerkProvider attaches a global Clerk instance during boot; we
        // reach for it here because module-scoped code (like Api.ts's
        // prepareHeaders) can't use React hooks.
        Clerk?: {
            session?: {
                getToken: (opts?: { template?: string }) => Promise<string | null>;
            };
        };
    }
}

/**
 * Ask Clerk for a JWT signed under our ila26-api template. Returns null
 * when Clerk isn't loaded yet or the user isn't signed in. Extracts the
 * `exp` claim from the JWT so callers can honor short-lived tokens.
 */
export async function fetchClerkAccessToken(): Promise<FrontendAccessToken | null> {
    const session = window.Clerk?.session;
    if (!session) return null;

    const token = await session.getToken({ template: clerkJwtTemplateName });
    if (!token) return null;

    const exp = decodeJwtExpSeconds(token);
    return { token, expiresOnTimestamp: exp * 1000 };
}

/**
 * Router used by RTK Query's prepareHeaders (services/api/Api.ts) and any
 * other module-scoped code that needs an API bearer. Picks Clerk or MSAL
 * based on the build-time feature flag. Same return shape as MSAL's
 * fetchAccessToken so no caller has to know which provider is active.
 */
export async function fetchApiAccessToken(): Promise<FrontendAccessToken | null> {
    if (isClerkEnabled) {
        return fetchClerkAccessToken();
    }
    const msalToken = await fetchMsalAccessToken();
    if (!msalToken) return null;
    return { token: msalToken.token, expiresOnTimestamp: msalToken.expiresOnTimestamp };
}

/**
 * Provider-agnostic view of the currently signed-in end user. Mirrors the
 * fields Ilan26 UI actually reads from an MSAL AccountInfo — `id`
 * (localAccountId <-> Clerk externalId, which the migration tool set to
 * ApplicationUser.Id), `name` (display name), `email`. Add fields here
 * (and populate them in both provider implementations below) rather than
 * reaching into provider-specific types at the call site.
 */
export interface AppAuthAccount {
    id: string;
    name?: string;
    email?: string;
}

function useCurrentAppAccountMsal(): AppAuthAccount | null {
    const { instance } = useMsal();
    const account = instance.getActiveAccount();
    if (!account) return null;
    return {
        id: account.localAccountId,
        name: account.name,
        email: account.username,
    };
}

function useCurrentAppAccountClerk(): AppAuthAccount | null {
    const { user, isSignedIn } = useClerkUser();
    if (!isSignedIn || !user) return null;
    // externalId is the ApplicationUser.Id we stamped during migration; if
    // it's missing (edge case: a Clerk-native user not seen by the migrator)
    // fall back to the Clerk id so we never hand back an empty string.
    return {
        id: user.externalId ?? user.id,
        name: [user.firstName, user.lastName].filter(Boolean).join(" ") || undefined,
        email: user.primaryEmailAddress?.emailAddress,
    };
}

/**
 * Returns the currently signed-in account, or null. Replaces
 * `useMsal().instance.getActiveAccount()` at the call site.
 *
 * Bound once at module init to the provider selected by isClerkEnabled —
 * the value never changes at runtime, so React's rules of hooks are
 * respected (same hook is called every render).
 */
export const useCurrentAppAccount: () => AppAuthAccount | null = isClerkEnabled
    ? useCurrentAppAccountClerk
    : useCurrentAppAccountMsal;

/**
 * Module-scoped variant for the rare place that reads
 * `msalInstance.getActiveAccount()` outside of a hook (e.g. the profile
 * menu reads the id before render to use it as a dependency-list key).
 * Returns null when Clerk isn't loaded / not signed in yet.
 */
export function getActiveAppAccountId(): string | undefined {
    if (isClerkEnabled) {
        // window.Clerk.user isn't in our declared type slice — reach for
        // it defensively; if it's not there we fall through to undefined.
        const clerkUser = (window.Clerk as unknown as { user?: { externalId?: string; id?: string } } | undefined)?.user;
        return clerkUser?.externalId ?? clerkUser?.id;
    }
    return msalInstance.getActiveAccount()?.localAccountId;
}

function useAppIsAuthenticatedMsal(): boolean {
    return useMsalIsAuthenticated();
}

function useAppIsAuthenticatedClerk(): boolean {
    // useAuth().isSignedIn is undefined while Clerk is still loading; treat
    // that as "not yet authed" so gated routes don't render before Clerk
    // decides. The <ClerkLoaded>/<ClerkLoading> wrap in App.tsx already
    // holds the app back during load, so in practice this branch only
    // returns false briefly on first paint.
    const { isSignedIn } = useClerkAuth();
    return isSignedIn === true;
}

/**
 * Returns true when the user has an active session on whichever provider
 * owns auth. Replaces `useIsAuthenticated()` from @azure/msal-react at
 * the route-file gate that decides whether authenticated routes should
 * be mounted at all.
 */
export const useAppIsAuthenticated: () => boolean = isClerkEnabled
    ? useAppIsAuthenticatedClerk
    : useAppIsAuthenticatedMsal;

function useAppSignOutMsal(): () => void {
    const { instance } = useMsal();
    return () => instance.logoutRedirect();
}

function useAppSignOutClerk(): () => void {
    const clerk = useClerk();
    return () => {
        void clerk.signOut({ redirectUrl: "/" });
    };
}

/**
 * Returns a callback that signs the user out via whichever provider owns
 * the session. Replaces `useMsal().instance.logoutRedirect()`. Same
 * one-time provider bind as useCurrentAppAccount.
 */
export const useAppSignOut: () => () => void = isClerkEnabled ? useAppSignOutClerk : useAppSignOutMsal;

/**
 * Raw Clerk user resource (or {user: null} on MSAL builds). Bound at module
 * init like the other compat hooks so React always sees a stable hook.
 * Needed where the app must read/write Clerk metadata directly (e.g. the
 * temp-password migration gate reading unsafeMetadata.requirePasswordChange).
 */
const useAppClerkUserNoop = () => ({ user: null as ReturnType<typeof useClerkUser>["user"] | null });
export const useAppClerkUser: () => { user: ReturnType<typeof useClerkUser>["user"] | null } =
  isClerkEnabled ? useClerkUser : useAppClerkUserNoop;

/**
 * Renders children only when a session is active. Replaces
 * `<AuthenticatedTemplate>`. In the MSAL path we delegate to the hook we
 * already have (returns null when no active account); in the Clerk path
 * we delegate to Clerk's own hook. Simple enough that a component using
 * <AuthenticatedContent> reads identically to what it read before.
 */
export function AuthenticatedContent({ children }: { children: ReactNode }) {
    const account = useCurrentAppAccount();
    return account ? createElement(Fragment, null, children) : null;
}

function decodeJwtExpSeconds(jwt: string): number {
    try {
        const [, payloadSegment] = jwt.split(".");
        if (!payloadSegment) return Math.floor(Date.now() / 1000) + 60;
        const padded = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
        const decoded = atob(padded + "===".slice((padded.length + 3) % 4));
        const payload = JSON.parse(decoded) as { exp?: number };
        return payload.exp ?? Math.floor(Date.now() / 1000) + 60;
    } catch {
        return Math.floor(Date.now() / 1000) + 60;
    }
}
